'use client';

import { useEffect, useState } from 'react';
import { Info } from 'lucide-react';
import { InfoModal } from '@/components/ui/InfoModal/InfoModal';
import {
  dismissGearTypePolicyModal,
  isGearTypePolicyModalDismissed,
} from '@/utils/gearTypePolicy';

const GearTypePolicyModal = () => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isGearTypePolicyModalDismissed()) {
      setIsOpen(true);
    }
  }, []);

  const dismiss = () => {
    dismissGearTypePolicyModal();
    setIsOpen(false);
  };

  return (
    <InfoModal
      open={isOpen}
      onClose={dismiss}
      icon={<Info size={24} className="text-white" />}
      title="New Gear Type & Format Policy"
      message={
        <div className="flex flex-col gap-4 text-zinc-400">
          <span>
            We&apos;ve updated how tones are categorized. Every tone now has a Gear (what was
            captured) and a Format (the file type).
          </span>
          <span className="text-zinc-200">The 7 Gears</span>
          <ul className="list-disc pl-6 flex flex-col gap-1">
            <li>
              <span className="text-zinc-200">Amp + Cab</span> (NAM). A complete amplified
              chain, a miked combo or amp head + cab, captured end-to-end.
            </li>
            <li>
              <span className="text-zinc-200">Amp Head</span> (NAM). Just the amplifier circuit
              (head or combo electronics), with no cab in the signal.
            </li>
            <li>
              <span className="text-zinc-200">Cabinet</span> (NAM or IR). A speaker cabinet captured
              with mics, no amp baked in.
            </li>
            <li>
              <span className="text-zinc-200">Pedal</span> (NAM or IR). Floor-format effects like
              drive, fuzz, distortion, delay, reverb pedal, EQ pedal, and multi-fx units.
            </li>
            <li>
              <span className="text-zinc-200">Outboard</span> (NAM or IR). Rack and mounted
              hardware processors like tape machines, EQs, compressors, and spring reverb units.
            </li>
            <li>
              <span className="text-zinc-200">Spaces</span> (IR). Real or simulated acoustic spaces
              like rooms, halls, cathedrals, garages, and stairwells.
            </li>
            <li>
              <span className="text-zinc-200">Experimental</span> (NAM or IR). Anything that
              doesn&apos;t fit, like toys, household objects, acoustic guitar emulations, and
              calibration files.
            </li>
          </ul>
          <span className="text-zinc-200">The 2 Formats</span>
          <ul className="list-disc pl-6 flex flex-col gap-1">
            <li>
              <span className="text-zinc-200">NAM</span> (Neural Amp Modeler) captures dynamic,
              nonlinear behavior.
            </li>
            <li>
              <span className="text-zinc-200">IR</span> (Impulse Response) captures linear,
              time-invariant systems.
            </li>
          </ul>
          <span>Some Gear and Format combinations don&apos;t make sense:</span>
          <ul className="list-disc pl-6 flex flex-col gap-1">
            <li>Amp or Amp + Cab as IR. IRs can&apos;t capture amp distortion, so use NAM.</li>
            <li>Spaces as NAM. Acoustic spaces are linear, so use IR.</li>
          </ul>
          <span>
            Some packs may have been re-categorized to match the new taxonomy. Check your packs to
            confirm, and check your email for details.
          </span>
        </div>
      }
    />
  );
};

export default GearTypePolicyModal;
